import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { useQuery } from '@tanstack/react-query';
import { noop } from 'lodash-es';

import { OwnerAndUnitManagerDto, useProductCenterApi } from 'api';
import {
	unitCampaignsQuery,
	unitConnectedEndCustomersQuery,
} from 'api/queries';
import { Id } from 'domain/Id';
import { DialogDataProps } from 'library/components/controls/buttons/DialogIconButton';
import { DialogCloseProps } from 'library/components/controls/dialogs/BaseDialog';
import {
	CloseButton,
	DrawerContainer,
} from 'library/components/controls/drawer/Drawer';
import {
	SectionWrapper,
	defaultSectionWrapperSx,
} from 'library/components/controls/layout/Section';
import { CustomerBox } from 'modules/EndCustomer/Shared/CustomerBox';
import {
	CampaignsDataGrid,
	useCampaignColumns,
} from 'modules/Shared/Components/Campaign/CampaignsDataGrid';
import { UnitInformation } from 'modules/Shared/Components/Unit/Information/UnitInformation';

import { ServiceAgreementDisplay } from './ServiceAgreementDisplay';

export interface UnitDetailViewData {
	queryAsExternal?: boolean;
}

interface UnitDetailViewProps
	extends DialogDataProps<UnitDetailViewData & Id>, DialogCloseProps {}

export const UnitDetailView = ({
	data: { id, queryAsExternal = false },
	onClose = noop,
}: UnitDetailViewProps) => {
	// only create this component, when displayed, remove button from it
	const { api } = useProductCenterApi();
	const { data } = useQuery<OwnerAndUnitManagerDto>(
		unitConnectedEndCustomersQuery(api, id)
	);
	const columns = useCampaignColumns();

	return (
		<Drawer open anchor="right" onClose={onClose}>
			<DrawerContainer>
				<CloseButton handleClose={onClose} />
				<UnitInformation id={id}>
					<SectionWrapper sx={{ pb: 3 }}>
						<CampaignsDataGrid
							columns={columns}
							displayAsInnerGrid
							queryOptions={unitCampaignsQuery(
								api,
								id,
								true,
								true,
								queryAsExternal ?? false
							)}
						/>
					</SectionWrapper>
					<SectionWrapper>
						<ServiceAgreementDisplay id={id} />
					</SectionWrapper>
					<SectionWrapper
						sx={{
							...defaultSectionWrapperSx,
							gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
						}}>
						<Box>
							<CustomerBox customer={data?.owner} isOwner />
						</Box>
						<Box>
							<CustomerBox
								customer={data?.unitManager}
								isOwner={false}
							/>
						</Box>
					</SectionWrapper>
				</UnitInformation>
			</DrawerContainer>
		</Drawer>
	);
};
